import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  IntlProvider,
  MessageFormatElement,
  MissingTranslationError,
  MessageFormatError,
  MissingDataError,
  InvalidConfigError,
  UnsupportedFormatterError,
} from "react-intl";
import { FormatError, FormatXMLElementFn } from "intl-messageformat";
import { getInitialUrlArgs } from "./UrlArgsProvider";

export type Messages = Record<string, string> | Record<string, MessageFormatElement[]>

export type EmbeddedMessages = {
  localeMessages: {
    [locale: string]: Messages
  };
};

export type RemoteMessages = {
  path: string
};

export type ExternalMessages = {
  fetch: () => Promise<Messages>;
}

function isRemote(
  data: ExternalMessages | EmbeddedMessages | RemoteMessages
): data is RemoteMessages {
  return (data as RemoteMessages).path !== undefined;
}

function isExternal(data: ExternalMessages | EmbeddedMessages | RemoteMessages): data is ExternalMessages {
  return (data as ExternalMessages).fetch !== undefined
}

interface IProps {
  data: ExternalMessages | EmbeddedMessages | RemoteMessages;
  locale?: string;
  defaultLocale?: string;
  children: ReactNode;
}

const MissingTranslations: { [id: string]: string } = {};

export const StringProvider = (props: IProps) => {
  const [messages, setMessages] = useState<
    Record<string, string> | Record<string, MessageFormatElement[]>
  >({});
  const { showTranslationIds } = getInitialUrlArgs();

  const selectedLocale = props.locale || props.defaultLocale;

  const loadLocaleMessages = useCallback(async () => {
    if (!selectedLocale) {
      return;
    }

    let messages:
      | Record<string, string>
      | Record<string, MessageFormatElement[]>
      | null = null;
    if (isExternal(props.data)) {
      try {
        messages = await props.data.fetch()
      }
      catch (ex: unknown){
        console.error(`Failed to load translations from external`, ex)
      }
    }
    else if (isRemote(props.data)) {
      const path = `${props.data.path}${selectedLocale}.json`
      try {
        const response = await fetch(path);
        if (!response.ok) {
          return;
        }
        messages = await response.json();
      }
      catch (ex: unknown){
        console.error(`Failed to load translations from ${path}`, ex)
      }
    }
    else {
      messages = props.data.localeMessages[selectedLocale];
    }

    if (messages !== null) {
      if (showTranslationIds) {
        messages = Object.keys(messages).reduce((m, c) => {
          m[c] = c;
          return m;
        }, {} as Record<string, string> | Record<string, MessageFormatElement[]>);
      }

      setMessages(messages);
    }
  }, [setMessages, props.data, selectedLocale, showTranslationIds]);

  useEffect(() => {
    loadLocaleMessages();
  }, [loadLocaleMessages]);

  const onError = (
    error:
      | MissingTranslationError
      | MessageFormatError
      | MissingDataError
      | InvalidConfigError
      | UnsupportedFormatterError
      | FormatError
  ) => {
    if(Object.keys(messages).length === 0) {
      return
    }
    if(error.code === "MISSING_TRANSLATION") {
      if (
        error.descriptor?.id &&
        MissingTranslations[error.descriptor.id] === undefined
      ) {
        MissingTranslations[error.descriptor.id] =
          error.descriptor.defaultMessage?.toString() ?? "";
        console.log(
          `${Object.keys(MissingTranslations).length} Missing Translations`,
          MissingTranslations
        );
      }
      return;
    }
    console.log(error);
  };

  const onWarning = (warning: string) => {
    console.log(warning);
  };

  const defaultRichTextElements: {
    [element: string]: FormatXMLElementFn<ReactNode>;
  } = {
    b: (chunks: ReactNode) => <b>{chunks}</b>,
    br: () => <br />,
    p: (chunks: ReactNode) => <p>{chunks}</p>,
  };

  return (
    <IntlProvider
      locale={selectedLocale || ""}
      messages={messages}
      defaultLocale={props.defaultLocale}
      defaultRichTextElements={defaultRichTextElements}
      onError={onError}
      onWarn={onWarning}
    >
      {props.children}
    </IntlProvider>
  );
};
